import { useState } from "react";
import { Check, Trash2, Upload, X } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { RejectNoteDialog } from "@/features/content/components/RejectNoteDialog";
import { useContentPermissions } from "@/features/content/hooks/useContentPermissions";
import {
  useApproveContent,
  useDeleteContent,
  usePublishContent,
  useRejectContent,
} from "@/features/content/hooks/useContentMutations";
import { hasPendingEdits } from "@/features/content/lib/content-review-status";
import { cn } from "@/lib/utils";
import type { ContentEntry } from "@/types/content";

function actionClass(tone: "primary" | "destructive") {
  const hover = tone === "destructive" ? "hover:text-destructive" : "hover:text-primary";
  return cn(
    "inline-flex items-center justify-center text-muted-foreground transition-colors",
    hover,
    "disabled:pointer-events-none disabled:opacity-40",
  );
}

interface ContentRowActionsProps {
  entry: ContentEntry;
}

type ActionKey = "approve" | "publish" | "reject" | "delete";

export function ContentRowActions({ entry }: ContentRowActionsProps) {
  const { canApprove, canPublish, canDeleteDraft } = useContentPermissions();
  const approve = useApproveContent();
  const publish = usePublishContent();
  const reject = useRejectContent();
  const remove = useDeleteContent();

  const [openAction, setOpenAction] = useState<ActionKey | null>(null);

  const ref = { contentType: entry.contentType, id: String(entry.id) };
  const isBusy =
    approve.isPending ||
    publish.isPending ||
    reject.isPending ||
    remove.isPending;
  const label = entry.title ?? "هذا المحتوى";

  const reviewStatus =
    typeof entry.reviewStatus === "string" ? entry.reviewStatus : "DRAFT";
  const isSubmitted = reviewStatus === "SUBMITTED";
  const isPublished = Boolean(entry.published);
  const pendingEdits = hasPendingEdits(entry);
  /*
   * 🔑 THE SERVER DECIDES THIS NOW. `publishesInOneClick` is computed by imof-backend from the whole
   * three-level permission model, and this component just renders it.
   *
   * ⚠️ THERE USED TO BE A CLIENT-SIDE MIRROR HERE AND IT WENT STALE, WHICH IS WHY IT IS GONE.
   * It read `passesReviewFloor = isReviewed || canPublish || canApprove` and
   * `passesDepartmentRule = !requiresApproval || reviewStatus === "APPROVED" || canApprove`, which
   * was an exact copy of the server rule ON THE DAY IT WAS WRITTEN. When the backend's department
   * tier gained its third path — a `content:publish` override written at THAT department for THAT
   * user — the copy could not follow, because /permissions/me returns a flat list of permission
   * NAMES and cannot express which SCOPE a grant came from. The result was the dashboard hiding نشر
   * from exactly the people who had been deliberately exempted.
   *
   * The lesson, and the reason this is not "fixed" by adding a fourth clause: a rule whose inputs
   * the client cannot see must not be evaluated on the client. Ask the server.
   *
   * ⚠️ ABSENT MEANS UNKNOWN, NOT FALSE. Mutation responses omit the field, and an older backend
   * would too. Offer the button and let the server refuse — never hide an action because a field is
   * missing. That failure direction is the whole bug being fixed here.
   */
  const canPublishNow = entry.publishesInOneClick !== false;

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      setOpenAction(null);
    } catch {
      /* handled by mutation onError */
    }
  };

  const showApprove = canApprove && isSubmitted;
  const showPublish =
    canPublish && canPublishNow && (!isPublished || pendingEdits);
  const showReject = canApprove && isSubmitted;
  /*
   * 🔄 A PUBLISHED PAGE IS DELETABLE (2026-09-17). `!isPublished` stood here and mirrored a server
   * refusal that has now gone with it — and while it stood, a published page could be deleted from
   * NOWHERE in this dashboard, because there is no unpublish action to reach the draft-only state
   * either end demanded. On a site of 236 migrated, already-published pages that is not a rail, it
   * is an inability to remove anything. Reported as "there is supposed to be a delete button for
   * every page in the list".
   *
   * 🔴 UNDER REVIEW STILL HIDES IT, and that is a different question from the public site: a
   * SUBMITTED page belongs to whoever was asked to review it until they answer, and «رفض» is how
   * you say no to one. The server refuses it too, so this is the affordance for a real rule.
   */
  const showDelete = canDeleteDraft && !isSubmitted;

  if (!showApprove && !showPublish && !showReject && !showDelete) return null;

  return (
    <div className="flex gap-2 items-center justify-end">
      {showApprove && (
        <ConfirmDialog
          open={openAction === "approve"}
          onOpenChange={(o) => setOpenAction(o ? "approve" : null)}
          // Approving now PUBLISHES as well (backend content.approve), so the copy has to say so —
          // an approver clicking this is making the page public, in every visible locale.
          // The backend gates the publish half on content:publish, so a reviewer who only holds
          // content:approve still just approves; the wording follows that.
          title={canPublish ? "تأكيد الموافقة والنشر" : "تأكيد الموافقة"}
          description={
            canPublish
              ? `سيتم اعتماد "${label}" ونشره وجعله مرئيًا للعامة بجميع اللغات. هل تريد المتابعة؟`
              : `هل أنت متأكد من الموافقة على "${label}"؟`
          }
          actionLabel={canPublish ? "موافقة ونشر" : "موافقة"}
          isLoading={approve.isPending}
          onConfirm={() => run(() => approve.mutateAsync(ref))}
          trigger={
            <button
              type="button"
              disabled={isBusy}
              aria-label={canPublish ? "موافقة ونشر" : "موافقة"}
              title={canPublish ? "موافقة ونشر" : "موافقة"}
              className={actionClass("primary")}
            >
              {approve.isPending ? (
                <Loader size="sm" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </button>
          }
        />
      )}

      {showPublish && (
        <ConfirmDialog
          open={openAction === "publish"}
          onOpenChange={(o) => setOpenAction(o ? "publish" : null)}
          title="تأكيد النشر"
          description={`سيتم نشر "${label}" وجعله مرئيًا للعامة. هل تريد المتابعة؟`}
          actionLabel="نشر"
          isLoading={publish.isPending}
          onConfirm={() => run(() => publish.mutateAsync(ref))}
          trigger={
            <button
              type="button"
              disabled={isBusy}
              aria-label={pendingEdits ? "نشر التعديلات" : "نشر"}
              title={pendingEdits ? "نشر التعديلات" : "نشر"}
              className={actionClass("primary")}
            >
              {publish.isPending ? (
                <Loader size="sm" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
            </button>
          }
        />
      )}

      {showReject && (
        <>
          <button
            type="button"
            disabled={isBusy}
            aria-label="رفض"
            title="رفض"
            className={actionClass("destructive")}
            onClick={() => setOpenAction("reject")}
          >
            {reject.isPending ? (
              <Loader size="sm" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </button>
          <RejectNoteDialog
            open={openAction === "reject"}
            onOpenChange={(o) => setOpenAction(o ? "reject" : null)}
            title={`هل أنت متأكد من رفض "${label}"؟`}
            isLoading={reject.isPending}
            onConfirm={(note) =>
              run(() => reject.mutateAsync({ ...ref, payload: { note } }))
            }
          />
        </>
      )}

      {showDelete && (
        <ConfirmDialog
          open={openAction === "delete"}
          onOpenChange={(o) => setOpenAction(o ? "delete" : null)}
          title="حذف الصفحة"
          /*
           * 🔑 A LIVE PAGE IS NAMED AS ONE. Deleting a draft and taking a page off the public site
           * are the same click now, and only the copy tells them apart — so the published case says
           * what the reader is about to do to the site, in every language the page has, rather than
           * leaving them to infer it from a status badge in another column.
           */
          description={
            isPublished
              ? `"${label}" منشورة على الموقع. سيؤدي الحذف إلى اختفائها من الموقع بجميع اللغات، ولا يمكن التراجع عن هذا الإجراء.`
              : `هل أنت متأكد من حذف "${label}"؟ لا يمكن التراجع عن هذا الإجراء.`
          }
          actionLabel="حذف"
          variant="destructive"
          isLoading={remove.isPending}
          onConfirm={() => run(() => remove.mutateAsync(ref))}
          trigger={
            <button
              type="button"
              disabled={isBusy}
              aria-label="حذف"
              title="حذف"
              className={actionClass("destructive")}
            >
              {remove.isPending ? (
                <Loader size="sm" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </button>
          }
        />
      )}
    </div>
  );
}
